"use client";

import React from "react";

export interface AtrSpecsDossierBlockProps {
    title?: string;
    specs?: { label: string; value: string; isAccent?: boolean }[];
    philosophyTag?: string;
    narrativeTitle?: string;
    narrativeParagraphs?: string[];
}

export default function AtrSpecsDossierBlock({
    title = "Project Specifications",
    specs = [
        { label: "Primary Engine", value: "Unity 3D Custom Flight Pipeline" },
        { label: "Telemetry Ingestion", value: "Recorded FDR parameter logs (CSV / Tabular)" },
        { label: "Monitored Avionics", value: "FDR Core Tickers, Engine Parameters, Control Columns" },
        { label: "Replication Tolerance", value: "Sub-second telemetry sync / <0.05% drift", isAccent: true },
        { label: "Primary Target Aircraft", value: "ATR 72-600 Turboprop" }
    ],
    philosophyTag = "// Product Philosophy",
    narrativeTitle = "Reconstructing flight mechanics, line by line.",
    narrativeParagraphs = [
        "Aviation analysis frequently struggles with the translation of long tables of numerical data from the Flight Data Recorder (FDR) into actionable, spatial visualizations. Our simulator solves this by mapping actual raw blackbox inputs directly to an exact digital twin of the ATR72-600.",
        "Built in Unity, the software processes 3D parameters including pitch, roll, heading, control column deflection, landing gear extension, flaps, and engine outputs. The simulation reconstructs past flight events in complete spatial clarity, enabling accident investigators, flight trainers, and engineers to walk through a flight step-by-step with absolute geometric accuracy."
    ]
}: AtrSpecsDossierBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0A0B0C] border-t border-b border-white/[0.04] w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                    
                    {/* Spec Panel */}
                    <div className="lg:col-span-4 bg-[#111214] border border-white/[0.06] p-8 rounded-xl shadow-xs">
                        <h3 className="font-cormorant font-serif text-2xl font-medium mb-6 text-white">
                            {title}
                        </h3>
                        <div className="space-y-5 text-sm font-sans">
                            {specs.map((spec, index) => (
                                <div key={index} className={index > 0 ? "border-t border-white/[0.05] pt-3" : ""}>
                                    <span className="text-[10px] uppercase font-mono text-white/40 tracking-widest block mb-1">
                                        {spec.label}
                                    </span>
                                    <span className={`font-medium ${spec.isAccent ? "text-[#00FF66] font-mono" : "text-white"}`}>
                                        {spec.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Overview Narrative */}
                    <div className="lg:col-span-8 space-y-8">
                        {philosophyTag && (
                            <span className="text-xs font-mono uppercase tracking-widest text-white/40 block">
                                {philosophyTag}
                            </span>
                        )}
                        <h2 className="font-cormorant font-serif text-4xl md:text-5xl font-light text-white leading-tight">
                            {narrativeTitle}
                        </h2>
                        <div className="space-y-6 text-white/60 leading-relaxed font-light text-base md:text-lg">
                            {narrativeParagraphs.map((para, i) => (
                                <p key={i}>{para}</p>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}
